"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "@/i18n/routing";
import Image from "next/image";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { Search, User, ShoppingBag, Menu, Heart, Bell, Calendar, Wrench, Gift } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { NAV_LINKS } from "@/lib/constants";
import { trackEvent } from "@/lib/track";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useTheme } from "@/context/ThemeContext";
import { readClientScope, getActiveScope, type QuickLinkScope } from "@/lib/quick-link-scope";
import MobileNav from "./MobileNav";
import LanguageSwitcher from "./LanguageSwitcher";
import CurrencySwitcher from "./CurrencySwitcher";

const SCOPE_FEATURE_LABELS: Record<string, string> = {
  book: "Book",
  accessories: "Accessories",
  service_pickup: "Service & Repair",
  gift_cards: "Gift Cards",
};

const SCOPE_FEATURE_HREFS: Record<string, string> = {
  book: "/book",
  accessories: "/accessories",
  service_pickup: "/account/service-pickup",
  gift_cards: "/gift-cards",
};

const SCOPE_FEATURE_ICONS: Record<string, typeof Calendar> = {
  book: Calendar,
  accessories: ShoppingBag,
  service_pickup: Wrench,
  gift_cards: Gift,
};

// Dynamically import SearchModal — only loaded when user opens search
const SearchModal = dynamic(() => import("./SearchModal"), { ssr: false });

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const { itemCount, toggleCart, cartBounceKey } = useCart();
  const [isBouncing, setIsBouncing] = useState(false);
  const prevBounceKey = useRef(cartBounceKey);
  const { wishlistCount } = useWishlist();
  const { data: session } = useSession();
  const [notificationCount, setNotificationCount] = useState(0);
  const { layout } = useTheme();
  const tCommon = useTranslations("common");
  const tNav = useTranslations("nav");
  const tCart = useTranslations("cart");
  const pathname = usePathname();
  const [scope, setScope] = useState<QuickLinkScope | null>(null);

  // Re-read scope cookie on every route change so an expired/cleared
  // cookie immediately restores the full nav. getActiveScope() also
  // ignores the cookie when the current path is OUTSIDE the scoped
  // flow — prevents the scoped UI from leaking onto the public site
  // after the customer navigates away from /shop/{token} (bug
  // 2026-04-29).
  useEffect(() => {
    setScope(getActiveScope(readClientScope(), pathname));
  }, [pathname]);

  // Exit-scope handler removed alongside the visible Exit pill —
  // customers were tapping it accidentally. The server endpoint at
  // /api/shop/exit-scope still exists for an admin path.

  const handleSearchOpen = useCallback(() => {
    trackEvent("search_opened");
    setIsSearchOpen(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setIsSearchOpen(false);
    // Restore focus to search trigger button after modal closes
    setTimeout(() => searchTriggerRef.current?.focus(), 50);
  }, []);

  // Bounce the cart icon when an item is added
  useEffect(() => {
    if (cartBounceKey !== prevBounceKey.current) {
      prevBounceKey.current = cartBounceKey;
      setIsBouncing(true);
      const timer = setTimeout(() => setIsBouncing(false), 500);
      return () => clearTimeout(timer);
    }
  }, [cartBounceKey]);

  useEffect(() => {
    // Hysteresis on the scroll-shrink toggle. Single-threshold variants
    // create a feedback loop on pages where the user lands with
    // scrollY ≈ threshold: header shrinks, content shifts UP, scrollY
    // drops below threshold, header expands, content shifts DOWN, and
    // we cross the threshold again — the bar shakes 3x/s. Using
    // separate thresholds (expand below 10, shrink above 80) gives a
    // wide dead zone the 700ms shrink animation can complete inside.
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled((prev) => {
        if (prev && y < 10) return false;
        if (!prev && y > 80) return true;
        return prev;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!session?.user) return;
    const fetchCount = () =>
      fetch("/api/account/notifications?unread=true&limit=1")
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setNotificationCount(data.unreadCount || 0); })
        .catch(() => {});
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    return () => clearInterval(interval);
  }, [session?.user]);

  // Lock body scroll when mobile nav or search is open
  useEffect(() => {
    if (isMobileNavOpen || isSearchOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileNavOpen, isSearchOpen]);

  // Check if a nav link is currently active
  const isLinkActive = (href: string) => {
    // Strip locale prefix for comparison
    const cleanPath = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
    const linkBase = href.split("?")[0];
    return cleanPath === linkBase || cleanPath.startsWith(linkBase + "/");
  };

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-40 transition-all duration-700 ease-out",
          // Transparent header (Maison, Noir): transparent bg, becomes solid on scroll
          layout.headerStyle === "transparent" && [
            "bg-white/95",
            isScrolled
              ? "shadow-[0_1px_20px_rgba(0,0,0,0.06)] backdrop-blur-md bg-white/90"
              : "backdrop-blur-none",
          ],
          // Solid header (Atelier, Bloom, Avenue): always solid bg + subtle border
          layout.headerStyle === "solid" && [
            "bg-background backdrop-blur-md border-b border-black/[0.06]",
            isScrolled && "shadow-[0_1px_20px_rgba(0,0,0,0.06)]",
          ],
          // Floating header (Jardin): floats with margin, rounded, shadow
          layout.headerStyle === "floating" && [
            "mx-3 sm:mx-6 mt-3 rounded-2xl",
            isScrolled
              ? "bg-white/95 backdrop-blur-md shadow-lg border border-black/[0.06]"
              : "bg-white/80 backdrop-blur-sm shadow-md border border-black/[0.04]",
          ]
        )}
      >
        <div
          className={cn(
            "container-luxury flex items-center justify-between transition-all duration-700 ease-out",
            isScrolled ? "py-2.5" : "py-3 sm:py-5"
          )}
        >
          {/* Left: Logo — under scope, points back to the link landing
              instead of the homepage. */}
          <Link
            href={scope ? scope.source : "/"}
            className="flex-shrink-0 hover:opacity-80 transition-opacity duration-500"
          >
            <Image
              src="/images/logo.png?v=2"
              alt="Rivky Wigs"
              width={140}
              height={70}
              className={cn(
                "transition-all duration-300",
                isScrolled ? "h-10 w-auto" : "h-10 sm:h-14 w-auto"
              )}
              priority
            />
          </Link>

          {/* Center: Desktop Navigation */}
          {scope ? (
            <nav className="hidden lg:flex items-center gap-6 xl:gap-10" aria-label={tNav("privateLinkAria")}>
              {scope.features.map((key) => {
                const href = SCOPE_FEATURE_HREFS[key];
                const label = SCOPE_FEATURE_LABELS[key];
                const Icon = SCOPE_FEATURE_ICONS[key];
                if (!href || !label) return null;
                const active = isLinkActive(href);
                return (
                  <Link
                    key={key}
                    href={href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-1.5 text-sm font-medium tracking-[0.15em] uppercase py-2 transition-colors duration-500",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2 rounded-sm",
                      active ? "text-brand-gold" : "text-brand-dark/75 hover:text-brand-dark"
                    )}
                  >
                    {Icon && <Icon className="h-3.5 w-3.5" />}
                    {label}
                  </Link>
                );
              })}
            </nav>
          ) : (
          <nav className="hidden lg:flex items-center gap-6 xl:gap-10" aria-label={tCommon("mainNavigation")}>
            {NAV_LINKS.map((link) => {
              const isDropdownOpen = activeDropdown === link.label;
              const isActive = isLinkActive(link.href);
              return (
                <div
                  key={link.label}
                  className="relative group"
                  onMouseEnter={() =>
                    link.children ? setActiveDropdown(link.label) : undefined
                  }
                  onMouseLeave={() => setActiveDropdown(null)}
                  onFocus={() =>
                    link.children ? setActiveDropdown(link.label) : undefined
                  }
                  onBlur={(e) => {
                    // Only close if focus leaves the entire nav item (including dropdown)
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setActiveDropdown(null);
                    }
                  }}
                >
                  <Link
                    href={link.href}
                    aria-expanded={link.children ? isDropdownOpen : undefined}
                    aria-haspopup={link.children ? "menu" : undefined}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => trackEvent("nav_click", { item: link.label.toLowerCase(), destination: link.href })}
                    onKeyDown={(e) => {
                      if (!link.children) return;
                      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                        e.preventDefault();
                        setActiveDropdown(isDropdownOpen ? null : link.label);
                      } else if (e.key === "Escape") {
                        setActiveDropdown(null);
                      }
                    }}
                    className={cn(
                      "text-sm font-medium tracking-[0.15em] uppercase transition-colors duration-500 py-2 whitespace-nowrap",
                      "relative",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2 rounded-sm",
                      isActive
                        ? "text-brand-gold"
                        : "text-brand-dark/75 hover:text-brand-dark"
                    )}
                  >
                    {tNav(link.tKey)}
                    {/* Animated underline */}
                    <span
                      className={cn(
                        "absolute -bottom-0.5 start-0 h-px bg-brand-gold transition-all duration-500 ease-out",
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      )}
                      aria-hidden="true"
                    />
                  </Link>

                  {/* Dropdown */}
                  {link.children && (
                    <div
                      role="menu"
                      aria-label={`${link.label} submenu`}
                      className={cn(
                        "absolute start-1/2 -translate-x-1/2 top-full pt-3 transition-all duration-300 ease-out",
                        isDropdownOpen
                          ? "opacity-100 visible translate-y-0"
                          : "opacity-0 invisible -translate-y-3 pointer-events-none"
                      )}
                    >
                      <div className="bg-white border border-brand-dark/5 shadow-xl rounded-xl p-2 min-w-[220px]">
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            href={child.href}
                            role="menuitem"
                            onKeyDown={(e) => {
                              if (e.key === "Escape") {
                                setActiveDropdown(null);
                              }
                            }}
                            className={cn(
                              "block px-5 py-3 text-sm text-brand-dark/70 rounded-lg",
                              "hover:text-brand-dark hover:bg-brand-cream",
                              "transition-all duration-500 ease-out whitespace-nowrap",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50"
                            )}
                          >
                            {tNav(child.tKey)}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          )}

          {/* Right: Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Language Switcher */}
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>

            {/* Currency Switcher */}
            <div className="hidden sm:block">
              <CurrencySwitcher />
            </div>

            {/* Search — hidden under scope (would surface out-of-scope products) */}
            {!scope && (
              <button
                ref={searchTriggerRef}
                onClick={handleSearchOpen}
                className={cn(
                  "p-2 sm:p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-brand-dark/60",
                  "hover:text-brand-gold hover:bg-brand-gold/5 hover:scale-110",
                  "transition-all duration-500 ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2"
                )}
                aria-label={tCommon("search")}
              >
                <Search className="h-[18px] w-[18px]" />
              </button>
            )}

            {/* Exit-scope button removed — customers were tapping it
                accidentally and falling out of their curated link.
                Scope still clears when the cookie expires (24h) or when
                they close the browser. Admin can manually clear via
                /api/shop/exit-scope if needed. */}

            {/* User — links to /account when logged in, /login when not */}
            <Link
              href={session?.user ? "/account" : "/login"}
              className={cn(
                "hidden sm:flex p-3 min-w-[44px] min-h-[44px] items-center justify-center rounded-full",
                session?.user ? "text-brand-gold" : "text-brand-dark/60",
                "hover:text-brand-gold hover:bg-brand-gold/5 hover:scale-110",
                "transition-all duration-500 ease-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2"
              )}
              aria-label={tNav("account")}
            >
              <User className="h-[18px] w-[18px]" />
            </Link>

            {/* Wishlist — hidden under scope */}
            {!scope && (
            <Link
              href="/account/wishlist"
              className={cn(
                "hidden sm:flex relative p-3 min-w-[44px] min-h-[44px] items-center justify-center rounded-full text-brand-dark/60",
                "hover:text-brand-gold hover:bg-brand-gold/5 hover:scale-110",
                "transition-all duration-500 ease-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2"
              )}
              aria-label={tCommon("wishlist")}
            >
              <Heart className="h-[18px] w-[18px]" />
              {wishlistCount > 0 && (
                <span className="absolute -top-0.5 -end-0.5 flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full bg-brand-gold text-white text-[10px] font-bold leading-none shadow-sm">
                  {wishlistCount > 9 ? "9+" : wishlistCount}
                </span>
              )}
            </Link>
            )}

            {/* Notifications */}
            {session?.user && (
              <Link
                href="/account/notifications"
                className={cn(
                  "hidden sm:flex relative p-3 min-w-[44px] min-h-[44px] items-center justify-center rounded-full text-brand-dark/60",
                  "hover:text-brand-gold hover:bg-brand-gold/5 hover:scale-110",
                  "transition-all duration-500 ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2"
                )}
                aria-label={tNav("notificationsAria")}
              >
                <Bell className="h-[18px] w-[18px]" />
                {notificationCount > 0 && (
                  <span className="absolute -top-0.5 -end-0.5 flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full bg-brand-gold text-white text-[10px] font-bold leading-none shadow-sm">
                    {notificationCount > 9 ? "9+" : notificationCount}
                  </span>
                )}
              </Link>
            )}

            {/* Cart */}
            <button
              onClick={toggleCart}
              className={cn(
                "relative p-2 sm:p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-brand-dark/60",
                "hover:text-brand-gold hover:bg-brand-gold/5 hover:scale-110",
                "transition-all duration-500 ease-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2",
                isBouncing && "animate-cart-bounce"
              )}
              aria-label={itemCount > 0 ? tCommon("cartWithItems", { count: itemCount }) : tCart("yourCart")}
            >
              <ShoppingBag className="h-[18px] w-[18px]" />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -end-0.5 flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full bg-brand-gold text-white text-[10px] font-bold leading-none shadow-sm" aria-hidden="true">
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileNavOpen(true)}
              className={cn(
                "lg:hidden p-2 sm:p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-brand-dark/60",
                "hover:text-brand-gold hover:bg-brand-gold/5 hover:scale-110",
                "transition-all duration-500 ease-out",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/50 focus-visible:ring-offset-2"
              )}
              aria-label={tCommon("openMenu")}
            >
              <Menu className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
      />

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={handleSearchClose}
      />
    </>
  );
}
